import type { ReactNode } from 'react';
import { CircleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { t } from '../i18n';

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}

/** Пусто — это не тупик: у каждого пустого состояния есть следующий шаг. */
export function EmptyState({ icon, title, description, action }: EmptyStateProps): JSX.Element {
  return (
    <div className="px-6 py-10 text-center text-muted-foreground">
      <div className="mb-3 flex justify-center">
        {icon ?? (
          <span className="num flex size-12 items-center justify-center rounded-full border border-dashed border-border-strong text-[18px] text-faint">
            0
          </span>
        )}
      </div>
      <div className="text-[16px] font-medium text-foreground">{title}</div>
      {description ? (
        <div className="mx-auto mt-1 max-w-[18rem] text-[13px] leading-snug">{description}</div>
      ) : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

interface ErrorStateProps {
  message: string;
  onRetry?: () => void;
}

/** Ошибка загрузки — блоком в потоке экрана, а не вместо всего экрана. */
export function ErrorState({ message, onRetry }: ErrorStateProps): JSX.Element {
  return (
    <div className="mb-4 flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/6 p-3.5">
      <CircleAlert className="mt-0.5 size-5 shrink-0 text-destructive" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="text-[14px] leading-snug text-foreground">{message}</div>
        {onRetry ? (
          <Button variant="outline" size="sm" className="mt-2.5" onClick={onRetry}>
            {t('components.retry')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
